import React, { createContext, useContext, useState } from 'react';
import Cookies from 'js-cookie';
import {
    clearAnonymousFavorites,
    clearAnonymousQuizHistory,
    getAnonymousFavorites,
    getAnonymousQuizHistory,
} from '../utils/AnonymousProgress';
import { getAnonymousImportUrl, getFavoriteQuestionPostUrl, getMySubscriptionUrl } from '../utils/UrlUtilities';
import { constructAnonymousImportPayload, getFavoriteQuestionPostPayload } from '../utils/PayloadUtils';
import { cookieOptions } from '../utils/BasicUtilities';
import { PLAN_CODES, SESSION_LOGOUT_EVENT_KEY } from '../utils/Constants';

// Create the context
const UserContext = createContext();

// Entitlement fields layered onto the session (populated from GET /api/me/subscription).
// Default everyone to FREE until the real subscription resolves.
const SUBSCRIPTION_DEFAULTS = {
    currentPlan: PLAN_CODES.FREE,
    entitlements: [],
    daysRemaining: 0,
    subscriptionStartsAt: null,
    subscriptionExpiresAt: null,
    upcoming: [],
};

const deriveRoleFlags = (userData) => {
    const role = userData?.user_role || userData?.role;
    return {
        isSuperAdmin: role === 'super_admin',
        isAdmin: role === 'admin' || role === 'super_admin',
        isUser: role === 'user',
    };
};

// Push localStorage favorites to the server after a fresh login/signup.
// Fire-and-forget — errors are logged but don't block auth UX.
// Uses raw fetch (not useApiCaller) because UserContext shouldn't reach into
// hooks; the JWT was just stored in cookies and is accepted as-is.
const migrateAnonymousFavorites = async (jwtToken) => {
    try {
        const ids = [...getAnonymousFavorites()];
        if (ids.length === 0) return;
        const url = getFavoriteQuestionPostUrl();
        const results = await Promise.allSettled(
            ids.map(id => fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-jwt': jwtToken,
                },
                body: JSON.stringify(getFavoriteQuestionPostPayload(id)),
            }))
        );
        const allOk = results.every(r => r.status === 'fulfilled' && r.value.ok);
        if (allOk) {
            clearAnonymousFavorites();
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Anonymous favorites migration failed:', err);
    }
};

// Push localStorage quiz history to the server after a fresh login/signup, so a
// guest's homepage quick-quiz attempts follow them as daily-quiz history (scores +
// streak) AND count toward the logged-in daily cap. Summary-only — no per-question
// data. Clears the local key on success. Raw fetch for the same reason as
// migrateAnonymousFavorites. Returns true if anything was imported.
const migrateAnonymousQuizHistory = async (jwtToken) => {
    try {
        const history = getAnonymousQuizHistory();
        if (!history || history.length === 0) return false;
        const res = await fetch(getAnonymousImportUrl(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-auth-jwt': jwtToken,
            },
            body: JSON.stringify(constructAnonymousImportPayload(history)),
        });
        if (res.ok) {
            clearAnonymousQuizHistory();
            return true;
        }
        return false;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Anonymous quiz history migration failed:', err);
        return false;
    }
};

// Fetch the user's current plan/entitlements. Raw fetch (same reasoning as the
// migrate helpers above — UserContext doesn't reach into hooks). Returns the mapped
// subscription slice, or null on failure (caller keeps existing/default values).
const fetchSubscription = async (jwtToken) => {
    try {
        const res = await fetch(getMySubscriptionUrl(), {
            headers: { 'x-auth-jwt': jwtToken },
        });
        if (!res.ok) return null;
        const data = await res.json();
        return {
            currentPlan: data.current_plan_code || PLAN_CODES.FREE,
            entitlements: data.entitlements || [],
            daysRemaining: data.days_remaining || 0,
            subscriptionStartsAt: data.current_starts_at || null,
            subscriptionExpiresAt: data.expires_at || null,
            upcoming: data.upcoming || [],
        };
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Subscription fetch failed:', err);
        return null;
    }
};

// Provider component
export const UserProvider = ({ children }) => {
    let userStateData = Cookies.get('user_state') ? JSON.parse(Cookies.get('user_state')) : undefined;
    if (!userStateData) {
        userStateData = {
            isLoggedIn: false,
            userId: null,
            email: '',
            name: '',
            isAdmin: false,
            isSuperAdmin: false,
            isUser: true,
            ...SUBSCRIPTION_DEFAULTS,
        }
    }
    const [userState, setUser] = useState(userStateData);
    // Bumped when a fresh login's anonymous quiz history finishes importing, so the
    // homepage quick-quiz counter re-fetches and reflects the merged attempts at once.
    const [anonImportTick, setAnonImportTick] = useState(0);

    // Run the post-auth migrations; signal the quiz-count consumer once the anon
    // history import lands so the daily cap reflects it without a manual reload.
    const runPostAuthMigrations = (jwtToken) => {
        migrateAnonymousFavorites(jwtToken);
        migrateAnonymousQuizHistory(jwtToken).then((imported) => {
            if (imported) setAnonImportTick((t) => t + 1);
        });
    };

    // Merge resolved subscription into state + the persisted cookie.
    const hydrateSubscription = async (jwtToken) => {
        const sub = await fetchSubscription(jwtToken);
        if (!sub) return;
        setUser((prev) => {
            const next = { ...prev, ...sub };
            Cookies.set('user_state', JSON.stringify(next), cookieOptions());
            return next;
        });
    };

    // Re-pull entitlements on demand (e.g. after a purchase/cancel in M2).
    const refreshSubscription = () => {
        const jwtToken = Cookies.get('jwt_token');
        if (jwtToken) hydrateSubscription(jwtToken);
    };

    const loginState = (userData) => {
        Cookies.set('jwt_token', userData.jwt_token, cookieOptions());
        Cookies.set('refresh_token', userData.refresh_token, cookieOptions());
        // A fresh login re-surfaces any competition notices the user had dismissed before.
        try { localStorage.removeItem('competition_notice_dismissed'); } catch (_) { /* ignore */ }
        const userStateData = {
            ...SUBSCRIPTION_DEFAULTS,
            ...userData,
            ...deriveRoleFlags(userData),
            isLoggedIn: true,
        };
        setUser(userStateData);
        Cookies.set('user_state', JSON.stringify(userStateData), cookieOptions());
        runPostAuthMigrations(userData.jwt_token);
        hydrateSubscription(userData.jwt_token);
    };

    const signupState = (userData) => {
        Cookies.set('jwt_token', userData.jwt_token, cookieOptions());
        Cookies.set('refresh_token', userData.refresh_token, cookieOptions());
        const userStateData = {
            ...SUBSCRIPTION_DEFAULTS,
            ...userData,
            ...deriveRoleFlags(userData),
            isLoggedIn: true,
        };
        setUser(userStateData);
        Cookies.set('user_state', JSON.stringify(userStateData), cookieOptions());
        runPostAuthMigrations(userData.jwt_token);
        hydrateSubscription(userData.jwt_token);
    };

    const logoutState = ({ broadcast = true } = {}) => {
        setUser({
            isLoggedIn: false,
            userId: null,
            email: '',
            name: '',
            isAdmin: false,
            isSuperAdmin: false,
            isUser: false,
            ...SUBSCRIPTION_DEFAULTS,
        });
        Cookies.remove('jwt_token');
        Cookies.remove('refresh_token');
        Cookies.remove('user_state');
        // Signal other open tabs to log out too. Skipped when this logout was itself
        // triggered by another tab's broadcast (prevents a cross-tab loop).
        if (broadcast) {
            try { localStorage.setItem(SESSION_LOGOUT_EVENT_KEY, String(Date.now())); } catch (_) { /* ignore */ }
        }
    };

    return (
        <UserContext.Provider value={{ userState, loginState, signupState, logoutState, setUser, refreshSubscription, anonImportTick }}>
        {children}
        </UserContext.Provider>
    );
};

// Hook to use the context
export const useUser = () => useContext(UserContext);
